// ─── Requisitos de Pliego ────────────────────────────────────────────────────
// Vista de trabajo colaborativo con el equipo redactor del proyecto para que
// los pliegos de licitación y de proyecto ejecutivo incluyan los requisitos
// BREEAM ES NC 2015 exigidos por el nivel objetivo del proyecto.
//
// Flujo: al entrar el usuario elige una de las 4 ramas (Diseño, Instalaciones,
// Contratista, HAUS asesoría) y accede a la lista de requisitos de esa rama.
// Cada requisito tiene tipo (Documento o Decisión), estado editable y
// permite adjuntar la plantilla HAUS y el documento final aportado por el
// equipo.
//
// Los datos viven en public.pliego_requisitos (migración 20260915).
// Los archivos viven en Storage: bucket `templates` (plantilla HAUS) y
// bucket `evidences` (documento final aportado).

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  CaretLeft, CaretRight, CaretDown, PencilSimple,
  Compass, Wrench, HardHat, ArrowsClockwise, Certificate,
  DownloadSimple, UploadSimple, Trash, Paperclip, MagnifyingGlass,
  CalendarBlank, Files as FilesIcon,
} from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import AG1Calculator from './AG1Calculator'

// Design tokens alineados con Informes/Monitorización
const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
}
const CARD = { background: C.bg, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const FONT = "'DM Sans', sans-serif"

const RAMAS = [
  {
    id: 'Diseño',
    label: 'Equipo de Diseño',
    hint: 'Coderch + Aires Mateus III · pliego arquitectónico y de interiorismo',
    icon: Compass, color: C.olive,
    // Cuando se rellena `logos`, la tarjeta reemplaza el icono Phosphor por
    // los logotipos reales del equipo (formato PNG con fondo transparente en
    // /public/logos/). Cada logo lleva alt y una altura fija para que la
    // tarjeta mantenga el mismo alto que las demás ramas.
    logos: [
      { src: '/logos/coderch.png',      alt: 'Coderch' },
      // Aires Mateus tiene el texto más pequeño de origen (logo 150x150),
      // así que aplicamos un factor de escala visual (transform: scale) para
      // que se lea con la misma presencia que Coderch, sin descuadrar la
      // línea inferior de la tarjeta.
      { src: '/logos/aires-mateus.png', alt: 'Aires Mateus III', scale: 1.9 },
    ],
  },
  {
    id: 'Instalaciones',
    label: 'Ingeniería de Instalaciones',
    hint: 'IIS Ingenieros · energía, agua, climatización, iluminación, transporte, acústica',
    icon: Wrench, color: C.olive,
    logos: [
      { src: '/logos/iis.png', alt: 'IIS Ingenieros' },
    ],
  },
  { id: 'Contratista',   label: 'Contratista',                hint: 'Pliego de licitación de obra · ejecución y post-obra',                   icon: HardHat,     color: C.olive },
  {
    id: 'HAUS',
    label: 'Consultoría',
    hint: 'Documentos y decisiones que asume directamente el asesor BREEAM',
    icon: Certificate, color: C.olive,
    logos: [
      { src: '/logos/haus.png', alt: 'HAUS Healthy Buildings' },
    ],
  },
]

// Altura fija de la cabecera de rama (logo/icono). Constante para que la
// línea horizontal separadora quede a la misma altura en las 4 tarjetas y
// se alineen los títulos entre sí.
const RAMA_HEADER_HEIGHT = 60

// Franja de logos centrada dentro de una caja de altura fija.
// Cuando un logo lleva `scale`, se aplica con transform (no toca el layout)
// para que el otro logo y la línea inferior de la tarjeta no se descuadren.
// Añadimos margen horizontal proporcional al scale para reservar el espacio
// visual extra y que no se pise con vecinos.
function LogosRama({ logos, height = RAMA_HEADER_HEIGHT, gap = '1.4rem' }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
      gap, height: `${height}px`,
    }}>
      {logos.map((logo, i) => {
        const scale = logo.scale || 1
        const baseH = height - 12
        // Compensación horizontal cuando el logo escala hacia los lados
        const marginX = scale > 1 ? `${Math.round((scale - 1) * 22)}px` : '0'
        return (
          <div key={logo.src} style={{ display: 'flex', alignItems: 'center', gap }}>
            {i > 0 && (
              <div style={{ width: '1px', height: `${Math.round(height * 0.55)}px`, background: '#D0C8BC' }} />
            )}
            <img
              src={logo.src}
              alt={logo.alt}
              style={{
                height: `${baseH}px`,
                width: 'auto', objectFit: 'contain', display: 'block',
                transform: scale !== 1 ? `scale(${scale})` : undefined,
                transformOrigin: 'center center',
                marginLeft: marginX, marginRight: marginX,
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

// Cabecera equivalente para ramas sin logo · icono Phosphor centrado en
// la misma caja de altura fija.
function IconoRama({ Icon, height = RAMA_HEADER_HEIGHT }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
      height: `${height}px`,
    }}>
      <Icon size={Math.round(height * 0.62)} weight="regular" color={C.olive} />
    </div>
  )
}

const ESTADOS = [
  { id: 'pendiente',   label: 'Pendiente',   color: C.coral },
  { id: 'en_revision', label: 'En revisión', color: C.amber },
  { id: 'auditado',    label: 'Auditado',    color: C.olive },
  { id: 'omitido',     label: 'Omitido',     color: C.muted },
]

const LABEL = { fontSize: '0.72rem', color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.4rem', fontFamily: FONT }
const BODY  = { fontSize: '0.85rem', color: C.text, lineHeight: 1.55, fontFamily: FONT }

// Extrae la coletilla "(Crit X)" o "(Crit X+Y)" del nombre para llevarla al badge.
function splitCritSuffix(name) {
  const m = (name || '').match(/^(.*?)\s*\((Crit[^)]+)\)\s*$/i)
  if (m) return { base: m[1].trim(), badge: m[2].trim() }
  return { base: name || '', badge: null }
}

// ── Encabezado estilo Informes ───────────────────────────────────────────────
function Header({ projectName, subtitleExtra }) {
  return (
    <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA' }}>
      <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: 500, fontFamily: FONT }}>
        Consultoría BREEAM · HAUS
      </div>
      <div style={{ fontSize: '1.2rem', fontWeight: 600, fontFamily: FONT }}>Requisitos de Pliego</div>
      <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem', fontFamily: FONT }}>
        {projectName ? projectName + ' · ' : ''}Lo que cada equipo debe incluir en su pliego para alcanzar el nivel BREEAM objetivo{subtitleExtra ? ' · ' + subtitleExtra : ''}.
      </div>
    </div>
  )
}

// ── Utilidades ───────────────────────────────────────────────────────────────
// El tipo (Documento/Decisión) vive en la columna 'tipo' de la tabla. Antes
// se guardaba dentro de comentario_asesor como marcador; la columna dedicada
// simplifica lectura y filtrado y libera comentario_asesor para su uso real.
function tipoFromItem(item) {
  return item.tipo || null
}

async function signedUrl(bucket, path) {
  const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60)
  return data?.signedUrl || null
}

/**
 * Descarga un archivo de Supabase Storage SIN mostrar la URL de Supabase.
 *
 * Hace fetch de la signed URL, obtiene el blob y lo entrega al usuario
 * como descarga con el nombre correcto. El usuario ve el archivo caer
 * en su carpeta de Descargas · nunca aparece la URL de storage en la
 * barra de direcciones ni en una nueva pestaña.
 */
async function descargarPrivado(bucket, path, filename) {
  const url = await signedUrl(bucket, path)
  if (!url) throw new Error('No se pudo generar la URL de descarga')
  const resp = await fetch(url)
  if (!resp.ok) throw new Error(`Descarga fallida (HTTP ${resp.status})`)
  const blob = await resp.blob()
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename || 'documento'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Liberar la URL blob tras un pequeño delay para que el download tenga tiempo de iniciar
  setTimeout(() => URL.revokeObjectURL(objUrl), 1000)
}

// ── Bloque de archivos · multi-documento por ficha ───────────────────────────
// El equipo del proyecto puede aportar VARIOS documentos por criterio
// (memoria + planos + fichas técnicas + certificados). Los archivos se guardan
// en el bucket `evidences` y las filas en la tabla `pliego_documentos` (1-N
// con pliego_requisitos).
//
// Se aplica un límite de 50 MB por archivo en el propio uploader (validación
// cliente) porque el bucket Supabase hereda ese límite del plan del proyecto.
// Si se necesita subir algo mayor, se sube troceado (varios docs).
const MAX_MB = 50

function formatSize(bytes) {
  if (!bytes || bytes <= 0) return ''
  const kb = bytes / 1024
  if (kb < 1000) return `${kb.toFixed(0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}
function formatDate(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString('es-ES') } catch { return '' }
}

function BloqueArchivos({ item, tipo, onDocumentoSubido }) {
  const [docs, setDocs] = useState([])
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [uploading, setUploading] = useState(false)
  const isDecision = tipo === 'Decisión'
  const emptyLabel = isDecision
    ? 'Sin documentos asociados. Añade presupuesto, carta de encargo o registro de la decisión.'
    : 'Sin documentos. El equipo puede añadir memoria, planos, fichas técnicas o certificados.'

  const cargarDocs = useCallback(async () => {
    setLoadingDocs(true)
    const { data, error } = await supabase
      .from('pliego_documentos')
      .select('*')
      .eq('pliego_requisito_id', item.id)
      .order('uploaded_at', { ascending: true })
    if (!error) setDocs(data || [])
    setLoadingDocs(false)
  }, [item.id])

  useEffect(() => { cargarDocs() }, [cargarDocs])

  const abrirDoc = async (doc) => {
    try {
      await descargarPrivado('evidences', doc.storage_path, doc.filename)
    } catch (err) {
      alert('Error al descargar: ' + err.message)
    }
  }

  const subirDoc = async (file) => {
    if (!file) return
    if (file.size > MAX_MB * 1024 * 1024) {
      alert(`El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB. El máximo por archivo son ${MAX_MB} MB. Súbelo troceado (por ejemplo memoria y planos en dos archivos).`)
      return
    }
    setUploading(true)
    try {
      const safe = file.name.replace(/[^\w.-]/g, '_')
      const path = `pliego/${item.project_id}/${item.id}/documento/${Date.now()}_${safe}`
      const { error: upErr } = await supabase.storage.from('evidences').upload(path, file, { upsert: false })
      if (upErr) throw upErr

      const { data: userData } = await supabase.auth.getUser()
      const userId = userData?.user?.id
      const { error: insErr } = await supabase.from('pliego_documentos').insert({
        pliego_requisito_id: item.id,
        project_id: item.project_id,
        user_id: userId,
        storage_path: path,
        filename: file.name,
        size_bytes: file.size,
        mime_type: file.type || null,
      })
      if (insErr) throw insErr

      await cargarDocs()
      // Auto-transición · si la ficha está pendiente y este es el primer
      // documento, pasa a en_revision para que HAUS sepa que hay que revisar.
      // No toca los estados en_revision ni auditado (respeta veredictos).
      if (item.estado === 'pendiente' && onDocumentoSubido) {
        onDocumentoSubido()
      }
    } catch (e) {
      alert('Error al subir archivo: ' + e.message)
    } finally {
      setUploading(false)
    }
  }

  const borrarDoc = async (doc) => {
    if (!window.confirm(`¿Quitar el documento «${doc.filename}»?`)) return
    await supabase.storage.from('evidences').remove([doc.storage_path])
    const { error } = await supabase.from('pliego_documentos').delete().eq('id', doc.id)
    if (error) alert('Error al borrar: ' + error.message)
    await cargarDocs()
  }

  const filaDoc = { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: C.bgAlt }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {loadingDocs && (
        <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FONT, fontStyle: 'italic' }}>
          Cargando documentos…
        </div>
      )}

      {!loadingDocs && docs.length === 0 && (
        <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FONT, fontStyle: 'italic', padding: '0.4rem 0' }}>
          {emptyLabel}
        </div>
      )}

      {!loadingDocs && docs.map(d => (
        <div key={d.id} style={filaDoc}>
          <Paperclip size={16} color={C.muted} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.82rem', color: C.text, fontFamily: FONT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
              {d.filename}
            </div>
            <div style={{ fontSize: '0.7rem', color: C.muted, fontFamily: FONT }}>
              {formatDate(d.uploaded_at)}{d.size_bytes ? ` · ${formatSize(d.size_bytes)}` : ''}
            </div>
          </div>
          <button onClick={() => abrirDoc(d)} title="Descargar" style={btnMini}>
            <DownloadSimple size={13} /> Ver
          </button>
          <button onClick={() => borrarDoc(d)} title="Quitar" style={btnMiniIcon}>
            <Trash size={13} color={C.coral} />
          </button>
        </div>
      ))}

      {/* Botón añadir · siempre visible, permite varios */}
      <label style={{
        ...btnMini, alignSelf: 'flex-start',
        cursor: uploading ? 'wait' : 'pointer',
        borderStyle: 'dashed', background: 'white',
      }}>
        <UploadSimple size={13} />
        {uploading ? 'Subiendo…' : docs.length === 0 ? 'Añadir primer documento' : 'Añadir otro documento'}
        <input type="file" hidden onChange={e => { subirDoc(e.target.files?.[0]); e.target.value = '' }} />
      </label>

      <div style={{ fontSize: '0.68rem', color: C.muted, fontFamily: FONT, fontStyle: 'italic' }}>
        Máximo {MAX_MB} MB por archivo. Puedes añadir tantos documentos como necesites.
      </div>
    </div>
  )
}

const btnMini = {
  padding: '0.35rem 0.7rem', background: 'white', color: C.text,
  border: `1px solid ${C.border}`, borderRadius: '4px', cursor: 'pointer',
  fontFamily: FONT, fontSize: '0.74rem', fontWeight: 500,
  display: 'inline-flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap',
}
const btnMiniIcon = {
  padding: '0.35rem 0.5rem', background: 'white',
  border: `1px solid ${C.border}`, borderRadius: '4px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center',
}

// ── Selector de estado ───────────────────────────────────────────────────────
function EstadoSelector({ value, onChange }) {
  const current = ESTADOS.find(e => e.id === value) || ESTADOS[0]
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      padding: '0.3rem 0.55rem', borderRadius: '4px',
      border: `1px solid ${C.border}`, background: 'white', color: current.color,
      fontFamily: FONT, fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer',
    }}>
      {ESTADOS.map(e => (<option key={e.id} value={e.id}>{e.label}</option>))}
    </select>
  )
}

// ── Utilidades de fecha límite y semáforo ────────────────────────────────────
// Devuelve { label, color } según la distancia hasta la fecha límite.
// Convención · vencido = rojo · <=15 días = ámbar · resto = oliva.
function urgenciaFecha(fechaISO) {
  if (!fechaISO) return null
  const hoy = new Date(); hoy.setHours(0,0,0,0)
  const fin = new Date(fechaISO); fin.setHours(0,0,0,0)
  const diasMs = 24 * 3600 * 1000
  const diff = Math.round((fin - hoy) / diasMs)
  if (diff < 0)  return { color: '#C0392B', label: `Vencida hace ${Math.abs(diff)}d`, bucket: 'vencidas', diff }
  if (diff === 0) return { color: '#C0392B', label: 'Vence hoy',                     bucket: 'esta_semana', diff }
  if (diff <= 7)  return { color: '#C98A2E', label: `${diff} d`,                    bucket: 'esta_semana', diff }
  if (diff <= 15) return { color: '#C98A2E', label: `${diff} d`,                    bucket: 'proximas_2sem', diff }
  if (diff <= 30) return { color: '#8D9A2E', label: `${diff} d`,                    bucket: 'este_mes', diff }
  return              { color: '#8D9A2E',    label: `${diff} d`,                    bucket: 'mas_adelante', diff }
}

function fechaCorta(fechaISO) {
  if (!fechaISO) return ''
  try {
    const d = new Date(fechaISO)
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' })
  } catch { return fechaISO }
}

// Editor de fecha límite · solo editable si isAdmin. El resto de usuarios ve
// la fecha en modo lectura con el color de urgencia.
function FechaLimiteEditor({ value, isAdmin, onSave }) {
  const urg = urgenciaFecha(value)
  if (!isAdmin) {
    if (!value) return null
    return (
      <div style={{
        padding: '0.3rem 0.55rem', borderRadius: '4px',
        border: `1px solid ${urg.color}`, background: 'white',
        color: urg.color, fontFamily: FONT, fontSize: '0.76rem',
        fontWeight: 600, display: 'inline-flex', alignItems: 'center',
        gap: '0.4rem', width: '100%',
      }}>
        <CalendarBlank size={12} />
        <span style={{ flex: 1 }}>{fechaCorta(value)}</span>
        <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>{urg.label}</span>
      </div>
    )
  }
  return (
    <div style={{
      padding: '0.2rem 0.45rem', borderRadius: '4px',
      border: `1px solid ${urg ? urg.color : C.border}`, background: 'white',
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem', width: '100%',
    }}>
      <CalendarBlank size={12} color={urg ? urg.color : C.muted} />
      <input
        type="date"
        value={value || ''}
        onChange={e => onSave(e.target.value || null)}
        title={urg ? urg.label : 'Sin fecha límite'}
        style={{
          border: 'none', outline: 'none', background: 'transparent',
          fontFamily: FONT, fontSize: '0.76rem',
          fontWeight: 600, color: urg ? urg.color : C.muted,
          flex: 1, cursor: 'pointer',
        }}
      />
      {value && (
        <button
          onClick={() => onSave(null)}
          title="Quitar fecha límite"
          style={{
            padding: 0, background: 'transparent', border: 'none',
            color: C.muted, cursor: 'pointer', fontSize: '0.9rem',
            lineHeight: 1,
          }}>×</button>
      )}
    </div>
  )
}

// ── Editor de responsable ────────────────────────────────────────────────────
// Campo libre para asignar la persona o empresa que asume la tarea. Cuando
// una rama incluye varios agentes (ej. Diseño = Coderch + Aires Mateus),
// permite decir claramente quién se encarga de cada requisito.
function ResponsableEditor({ value, onSave }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  useEffect(() => { setDraft(value || '') }, [value])
  const handleSave = () => {
    onSave(draft.trim() || null)
    setEditing(false)
  }
  if (!editing) {
    // Mismo formato que EstadoSelector · padding, borde sólido y tipografía
    // iguales para que ambos controles queden visualmente alineados.
    return (
      <button
        onClick={() => { setEditing(true); setDraft(value || '') }}
        title={value ? `Cambiar responsable · ${value}` : 'Asignar responsable de esta tarea'}
        style={{
          padding: '0.3rem 0.55rem', borderRadius: '4px',
          background: 'white',
          color: value ? C.text : C.muted,
          border: `1px solid ${C.border}`,
          fontFamily: FONT, fontSize: '0.76rem', fontWeight: 600,
          cursor: 'pointer', textAlign: 'left',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          minWidth: '100%',
        }}>
        {value || '+ responsable'}
      </button>
    )
  }
  return (
    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', width: '100%' }}>
      <input
        type="text"
        value={draft}
        placeholder="Nombre o empresa"
        autoFocus
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') setEditing(false)
        }}
        style={{
          padding: '0.3rem 0.55rem', borderRadius: '4px',
          border: `1px solid ${C.border}`, background: 'white', color: C.text,
          fontFamily: FONT, fontSize: '0.76rem', fontWeight: 500,
          flex: 1, minWidth: 0,
        }}
      />
      <button onClick={handleSave} title="Guardar (Enter)" style={{
        padding: '0.3rem 0.55rem', background: C.olive, color: 'white',
        border: 'none', borderRadius: '4px', cursor: 'pointer',
        fontFamily: FONT, fontSize: '0.72rem', fontWeight: 600,
      }}>OK</button>
      <button onClick={() => setEditing(false)} title="Cancelar (Esc)" style={{
        padding: '0.3rem 0.45rem', background: 'transparent', color: C.muted,
        border: `1px solid ${C.border}`, borderRadius: '4px', cursor: 'pointer',
        fontFamily: FONT, fontSize: '0.72rem',
      }}>×</button>
    </div>
  )
}

// ── Ficha de requisito ───────────────────────────────────────────────────────
function RequisitoCard({ item, onUpdate, isAdmin }) {
  const [expanded, setExpanded] = useState(false)
  const [editComment, setEditComment] = useState(false)
  const [comment, setComment] = useState(item.comentario_asesor || '')
  const estado = ESTADOS.find(e => e.id === item.estado) || ESTADOS[0]
  const tipo = tipoFromItem(item)

  const saveComment = async () => {
    await onUpdate(item.id, { comentario_asesor: comment })
    setEditComment(false)
  }

  const { base: tituloLimpio, badge: critBadge } = splitCritSuffix(item.criteria_name || '')

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderLeft: `4px solid ${estado.color}`,
      borderRadius: '6px', background: C.bgAlt,
      marginBottom: '0.6rem', padding: '0.9rem 1.05rem', fontFamily: FONT,
    }}>
      {/* Cabecera compacta */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
            <span style={{
              padding: '2px 8px', borderRadius: '3px',
              background: 'white', color: C.text, border: `1px solid ${C.border}`,
              fontSize: '0.72rem', fontWeight: 700,
            }}>
              {item.criteria_code}{critBadge ? ` · ${critBadge}` : ''}
            </span>
            {tipo === 'Documento' && item.documento_url && (
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  try { await descargarPrivado('templates', item.documento_url, item.documento_filename) }
                  catch (err) { alert('Error al descargar: ' + err.message) }
                }}
                title={`Descargar guía HAUS de ayuda a la interpretación del criterio BREEAM · ${item.documento_filename || ''}`}
                style={{
                  padding: '2px 8px', borderRadius: '3px',
                  background: 'white', color: C.text, border: `1px solid ${C.border}`,
                  fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  fontFamily: FONT,
                }}
                onMouseOver={e => { e.currentTarget.style.background = C.bgAlt }}
                onMouseOut={e => { e.currentTarget.style.background = 'white' }}
              >
                <DownloadSimple size={11} /> Guía
              </button>
            )}
            {tipo === 'Documento' && item.plantilla_url && (
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  try { await descargarPrivado('templates', item.plantilla_url, item.plantilla_filename) }
                  catch (err) { alert('Error al descargar: ' + err.message) }
                }}
                title={`Descargar plantilla rellenable · ${item.plantilla_filename || ''}`}
                style={{
                  padding: '2px 8px', borderRadius: '3px',
                  background: 'white', color: C.text, border: `1px solid ${C.border}`,
                  fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  fontFamily: FONT,
                }}
                onMouseOver={e => { e.currentTarget.style.background = C.bgAlt }}
                onMouseOut={e => { e.currentTarget.style.background = 'white' }}
              >
                <DownloadSimple size={11} /> Plantilla
              </button>
            )}
            {tipo === 'Documento' && !item.documento_url && !item.plantilla_url && (
              <span
                title="Ficha de tipo documento · sin guía ni plantilla adjunta todavía"
                style={{
                  padding: '2px 8px', borderRadius: '3px',
                  background: 'white', color: C.muted, border: `1px dashed ${C.border}`,
                  fontSize: '0.7rem', fontWeight: 600,
                }}>Guía</span>
            )}
            {item.checklist_url && (
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  try { await descargarPrivado('templates', item.checklist_url, item.checklist_filename) }
                  catch (err) { alert('Error al descargar: ' + err.message) }
                }}
                title={`Descargar lista de comprobación · ${item.checklist_filename || ''}`}
                style={{
                  padding: '2px 8px', borderRadius: '3px',
                  background: 'white', color: C.olive, border: `1px solid ${C.olive}`,
                  fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  fontFamily: FONT,
                }}
                onMouseOver={e => { e.currentTarget.style.background = C.bgAlt }}
                onMouseOut={e => { e.currentTarget.style.background = 'white' }}
              >
                <DownloadSimple size={11} /> {item.checklist_titulo || 'Lista'}
              </button>
            )}
            {item.consultoria_url && (
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  const raw = item.consultoria_url || ''
                  // URL absoluta (http/https): abrir directo en nueva pestaña.
                  // Usado para informes HAUS con assets externos (p.ej. materialidad
                  // AIRES con PDFs en appbyhaus.com) que no caben en el bucket.
                  if (/^https?:\/\//i.test(raw)) {
                    window.open(raw, '_blank', 'noopener,noreferrer')
                    return
                  }
                  // consultoria_url puede llevar un fragmento '#seccion' para saltar
                  // a un ancla dentro del HTML. Lo separamos antes del signed URL.
                  const hashIdx = raw.indexOf('#')
                  const objectPath = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw
                  const hash       = hashIdx >= 0 ? raw.slice(hashIdx) : ''
                  const { data } = await supabase.storage.from('consultoria-haus')
                    .createSignedUrl(objectPath, 3600)
                  if (!data?.signedUrl) return
                  // Supabase Storage sirve HTML como text/plain cuando el bucket es
                  // privado (política de seguridad · previene XSS). Para que el HTML
                  // renderice como página, lo descargamos y envolvemos en un Blob
                  // con MIME correcto antes de abrirlo.
                  const fname = (item.consultoria_filename || '').toLowerCase()
                  const isHtml = fname.endsWith('.html') || fname.endsWith('.htm')
                                 || objectPath.toLowerCase().endsWith('.html')
                                 || objectPath.toLowerCase().endsWith('.htm')
                  if (isHtml) {
                    try {
                      const resp = await fetch(data.signedUrl)
                      let text = await resp.text()
                      // Los blob URLs no tienen origen, así que las <img
                      // src="logo-haus.png"> (relativas) o src="/logo.png"
                      // (absolutas) no resuelven y salen rotas. Reescribimos
                      // los atributos src de img/script/iframe/etc. y href de
                      // <link> (stylesheets) para que apunten al dominio de
                      // la app. Excluimos los que ya son URLs completas
                      // (http/https/data/blob/protocol-relative) o anchors.
                      // Deliberadamente NO tocamos <a href> para no romper
                      // anchor links internos ni redirigir a la app cuando el
                      // HTML tenga enlaces relativos (ej. botón "Materiales").
                      const origin = window.location.origin
                      const skipProto = '(?!(?:https?:|data:|blob:|mailto:|tel:|javascript:|#|//))'
                      text = text.replace(
                        new RegExp(
                          `(<(?:img|script|iframe|source|audio|video)\\b[^>]*?\\ssrc\\s*=\\s*["'])${skipProto}\\/?([^"']*)`,
                          'gi'
                        ),
                        `$1${origin}/$2`
                      )
                      text = text.replace(
                        new RegExp(
                          `(<link\\b[^>]*?\\shref\\s*=\\s*["'])${skipProto}\\/?([^"']*)`,
                          'gi'
                        ),
                        `$1${origin}/$2`
                      )
                      // Los blob URLs no respetan el fragmento #hash en muchos
                      // navegadores. Además, el mapa AIRES oculta las tarjetas
                      // de oportunidad por defecto y solo las muestra cuando
                      // se invoca showOpDetail(cardId). Inyectamos un script
                      // que, al cargar, revela la sección y hace scroll.
                      const target = hash.replace('#', '')
                      if (target) {
                        // Nota: la etiqueta de cierre se compone con String.raw
                        // para evitar que el parser confunda con el fin de
                        // script del propio bundle.
                        const closeTag = String.raw`</script>`
                        const injected = `
<script>
(function() {
  var HASH = ${JSON.stringify(target)};
  function openAncestors(el) {
    // Abre el propio elemento si es <details> y todos sus <details> padres
    var node = el;
    while (node && node !== document.body) {
      if (node.tagName === 'DETAILS' && !node.open) { node.open = true; }
      node = node.parentElement;
    }
  }
  function go() {
    var el = document.getElementById(HASH);
    if (!el) return;
    // Tarjetas del bloque "Oportunidades" (op-*): revelar con la función interna
    if (typeof showOpDetail === 'function' && HASH.indexOf('op-') === 0) {
      try { showOpDetail(HASH); } catch (e) {}
    }
    // Si el destino está dentro de <details>, abrir todos los <details> padres
    openAncestors(el);
    setTimeout(function() { el.scrollIntoView({behavior:'smooth', block:'start'}); }, 250);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(go, 200); });
  } else {
    setTimeout(go, 200);
  }
  window.addEventListener('load', function() { setTimeout(go, 300); });
})();
${closeTag}`
                        if (text.indexOf('</body>') >= 0) {
                          text = text.replace('</body>', injected + '</body>')
                        } else {
                          text = text + injected
                        }
                      }
                      const blob = new Blob([text], { type: 'text/html; charset=utf-8' })
                      window.open(URL.createObjectURL(blob), '_blank')
                    } catch (err) {
                      alert('Error al abrir el HTML: ' + err.message)
                    }
                  } else {
                    window.open(data.signedUrl + hash, '_blank')
                  }
                }}
                title={`Abrir consultoría HAUS · ${item.consultoria_filename || ''}`}
                style={{
                  padding: '2px 8px', borderRadius: '3px',
                  background: 'white', color: C.text, border: `1px solid ${C.olive}`,
                  fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  fontFamily: FONT,
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#F5FAF0' }}
                onMouseOut={e => { e.currentTarget.style.background = 'white' }}
              >
                <MagnifyingGlass size={11} color={C.olive} /> Consultoría
              </button>
            )}
          </div>
          <div style={{ fontSize: '0.9rem', color: C.text, fontWeight: 600 }}>
            {tituloLimpio || item.criteria_code}
          </div>
          {item.fase_exigencia && (
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.15rem', fontFamily: FONT }}>
              ({item.fase_exigencia})
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', minWidth: '200px' }}>
          <EstadoSelector value={item.estado} onChange={v => onUpdate(item.id, { estado: v })} />
          <ResponsableEditor value={item.responsable} onSave={v => onUpdate(item.id, { responsable: v })} />
          <FechaLimiteEditor
            value={item.fecha_limite}
            isAdmin={isAdmin}
            onSave={v => onUpdate(item.id, { fecha_limite: v })}
          />
        </div>
      </div>

      {/* Botón expandir */}
      <div style={{ marginTop: '0.5rem' }}>
        <button onClick={() => setExpanded(!expanded)} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: C.muted, fontFamily: FONT, fontSize: '0.76rem', padding: 0,
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        }}>
          {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
          {expanded ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
      </div>

      {/* Detalle expandido · 3 secciones + archivos + comentario */}
      {expanded && (
        <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: `1px dashed ${C.border}`, display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {item.requisito_breeam && (
            <section>
              <div style={LABEL}>Requisito</div>
              <div style={BODY}>{item.requisito_breeam}</div>
            </section>
          )}
          {item.estado_actual_proyecto && (
            <section>
              <div style={LABEL}>Estado actual</div>
              <div style={BODY}>{item.estado_actual_proyecto}</div>
            </section>
          )}

          <section>
            <div style={LABEL}>Archivos</div>
            <BloqueArchivos
              item={item}
              tipo={tipo}
              onDocumentoSubido={() => onUpdate(item.id, { estado: 'en_revision' })}
            />
          </section>

          {/* Calculadora AG 1 embebida · modo simulador (sandbox) para que arquitectos prueben alternativas */}
          {item.criteria_code === 'AG 1' && (
            <section>
              <div style={LABEL}>Simulador BREEAM AG 1 · consumo de agua</div>
              <AG1Calculator
                projectId={item.project_id}
                sandbox={true}
                onSave={() => {}}
                saving={false}
              />
            </section>
          )}

          <section>
            <div style={{ ...LABEL, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Comentario del asesor</span>
              {!editComment && (
                <button onClick={() => { setEditComment(true); setComment(item.comentario_asesor || '') }}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                  <PencilSimple size={12} /> Editar
                </button>
              )}
            </div>
            {editComment ? (
              <div>
                <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} style={{
                  width: '100%', padding: '0.5rem', fontFamily: FONT, fontSize: '0.82rem',
                  border: `1px solid ${C.border}`, borderRadius: '4px', resize: 'vertical', color: C.text,
                }} />
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
                  <button onClick={saveComment} style={{
                    padding: '0.35rem 0.8rem', background: C.olive, color: 'white',
                    border: 'none', borderRadius: '4px', cursor: 'pointer',
                    fontFamily: FONT, fontSize: '0.76rem', fontWeight: 600,
                  }}>Guardar</button>
                  <button onClick={() => setEditComment(false)} style={{
                    padding: '0.35rem 0.8rem', background: 'transparent', color: C.muted,
                    border: `1px solid ${C.border}`, borderRadius: '4px', cursor: 'pointer',
                    fontFamily: FONT, fontSize: '0.76rem',
                  }}>Cancelar</button>
                </div>
              </div>
            ) : (
              <div style={{ ...BODY, color: item.comentario_asesor ? C.text : C.muted, fontStyle: item.comentario_asesor ? 'normal' : 'italic' }}>
                {item.comentario_asesor || 'Sin comentarios · pulsa Editar para añadir.'}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}

// ── Vista Agenda · fichas agrupadas por urgencia de la fecha límite ──────────
const AGENDA_BUCKETS = [
  { key: 'vencidas',      label: 'Vencidas',            color: '#C0392B' },
  { key: 'esta_semana',   label: 'Esta semana',         color: '#C98A2E' },
  { key: 'proximas_2sem', label: 'Próximas 2 semanas',  color: '#C98A2E' },
  { key: 'este_mes',      label: 'Este mes',            color: '#8D9A2E' },
  { key: 'mas_adelante',  label: 'Más adelante',        color: '#8D9A2E' },
  { key: 'sin_fecha',     label: 'Sin fecha asignada',  color: C.muted },
]

function AgendaView({ items, onBack, onOpenRama, isAdmin, onUpdate }) {
  // Agrupar cada ficha en su bucket temporal
  const grouped = new Map(AGENDA_BUCKETS.map(b => [b.key, []]))
  items.forEach(it => {
    if (!it.fecha_limite) { grouped.get('sin_fecha').push(it); return }
    const urg = urgenciaFecha(it.fecha_limite)
    grouped.get(urg.bucket).push(it)
  })
  // Ordenar dentro de cada bucket por fecha ascendente
  grouped.forEach(list => list.sort((a, b) => (a.fecha_limite || '').localeCompare(b.fecha_limite || '')))

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: `1px solid ${C.border}` }}>
        <CalendarBlank size={22} color={C.olive} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT }}>
            Agenda del pliego
          </div>
          <div style={{ fontSize: '0.74rem', color: C.muted, fontFamily: FONT }}>
            Fichas agrupadas por fecha límite. {isAdmin ? 'Puedes editar la fecha entrando en cada rama.' : 'Solo el asesor puede asignar fechas.'}
          </div>
        </div>
        <button onClick={onBack} style={{
          padding: '0.35rem 0.7rem', background: 'transparent',
          color: C.muted, border: `1px solid ${C.border}`,
          borderRadius: '4px', cursor: 'pointer',
          fontFamily: FONT, fontSize: '0.76rem',
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        }}>
          <CaretLeft size={12} /> Volver
        </button>
      </div>

      {AGENDA_BUCKETS.map(b => {
        const list = grouped.get(b.key) || []
        if (list.length === 0) return null
        return (
          <section key={b.key} style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: b.color }} />
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: C.text, fontFamily: FONT }}>
                {b.label}
              </div>
              <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT }}>
                · {list.length} {list.length === 1 ? 'ficha' : 'fichas'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {list.map(it => {
                const urg = it.fecha_limite ? urgenciaFecha(it.fecha_limite) : null
                const estado = ESTADOS.find(e => e.id === it.estado) || ESTADOS[0]
                return (
                  <div
                    key={it.id}
                    onClick={() => onOpenRama(it.rama)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.7rem',
                      padding: '0.6rem 0.85rem',
                      border: `1px solid ${C.border}`,
                      borderLeft: `4px solid ${b.color}`,
                      borderRadius: '4px', background: C.bgAlt,
                      cursor: 'pointer', fontFamily: FONT,
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#FFFFFF' }}
                    onMouseOut={e => { e.currentTarget.style.background = C.bgAlt }}
                  >
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: C.text, minWidth: '55px' }}>
                      {it.criteria_code}
                    </span>
                    <span style={{ flex: 1, fontSize: '0.82rem', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.criteria_name}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: C.muted, minWidth: '110px', textAlign: 'right' }}>
                      {it.rama}{it.responsable ? ` · ${it.responsable}` : ''}
                    </span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: estado.color, minWidth: '80px', textAlign: 'right' }}>
                      {estado.label}
                    </span>
                    {urg && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: urg.color, minWidth: '100px', textAlign: 'right' }}>
                        {fechaCorta(it.fecha_limite)} · {urg.label}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ── Componente principal ─────────────────────────────────────────────────────
// ─── Panel desplegable · Documentos subidos al pliego ──────────────────────
// Aparece arriba de la vista del pliego (dentro y fuera de rama seleccionada).
// Lista todos los documentos subidos a las fichas visibles para el usuario,
// ordenados por fecha (más recientes arriba). RLS de pliego_documentos ya
// garantiza que los invitados solo vean los de sus ramas.
function ListaDocumentosSubidos({ projectId, items }) {
  const [docs, setDocs] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [usersMap, setUsersMap] = useState({})
  const [q, setQ] = useState('')

  const cargar = useCallback(async () => {
    if (!projectId) return
    const { data, error } = await supabase
      .from('pliego_documentos')
      .select('id, filename, storage_path, size_bytes, uploaded_at, user_id, pliego_requisito_id')
      .eq('project_id', projectId)
      .order('uploaded_at', { ascending: false })
      .limit(500)
    if (!error) {
      setDocs(data || [])
      // Cargar nombres de los usuarios que subieron algo
      const uids = Array.from(new Set((data || []).map(d => d.user_id).filter(Boolean)))
      if (uids.length) {
        const { data: profs } = await supabase
          .from('user_profiles')
          .select('user_id, full_name')
          .in('user_id', uids)
        const map = {}
        for (const p of (profs || [])) map[p.user_id] = p.full_name || ''
        setUsersMap(map)
      }
    }
  }, [projectId])

  useEffect(() => { if (expanded && docs === null) cargar() }, [expanded, docs, cargar])

  // Indexar fichas para pintar código/rama junto a cada archivo.
  const fichaIdx = useMemo(() => {
    const m = {}
    for (const it of (items || [])) m[it.id] = it
    return m
  }, [items])

  const total = docs?.length ?? 0

  const abrirDoc = async (path) => {
    if (!path) return
    const { data } = await supabase.storage.from('evidences').createSignedUrl(path, 3600)
    if (!data?.signedUrl) return
    try {
      const resp = await fetch(data.signedUrl)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch {
      window.open(data.signedUrl, '_blank')
    }
  }

  const fmtBytes = (n) => {
    if (!n) return ''
    if (n < 1024) return n + ' B'
    if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB'
    return (n / 1024 / 1024).toFixed(1) + ' MB'
  }

  const ramaLabel = (r) => r === 'Instalaciones' ? 'Ingeniería' : (r || '—')

  // Filtro por texto libre sobre archivo, código de ficha, rama y nombre
  // del subidor. Se aplica solo cuando expandido y hay documentos.
  const nq = (q || '').toLowerCase().trim()
  const docsFiltered = useMemo(() => {
    if (!docs) return null
    if (!nq) return docs
    return docs.filter(d => {
      const f = fichaIdx[d.pliego_requisito_id] || {}
      const uname = (usersMap[d.user_id] || '').toLowerCase()
      return (
        (d.filename || '').toLowerCase().includes(nq) ||
        (f.criteria_code || '').toLowerCase().includes(nq) ||
        (f.criteria_name || '').toLowerCase().includes(nq) ||
        ramaLabel(f.rama).toLowerCase().includes(nq) ||
        uname.includes(nq)
      )
    })
  }, [docs, nq, fichaIdx, usersMap])

  return (
    <details open={expanded} onToggle={(e) => setExpanded(e.currentTarget.open)}
             style={{ ...CARD, padding: 0 }}>
      <summary style={{
        cursor: 'pointer', padding: '0.85rem 1.15rem',
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        listStyle: 'none', fontFamily: FONT,
      }}>
        <FilesIcon size={17} color={C.olive} />
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: C.text, flex: 1 }}>
          Documentos subidos al pliego
          {docs && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 500, color: C.muted }}>
              ({total})
            </span>
          )}
        </div>
        <span style={{ fontSize: '0.72rem', color: C.muted }}>
          {expanded ? 'ocultar' : 'ver lista'}
        </span>
      </summary>
      <div style={{ padding: '0 1.15rem 1.15rem' }}>
        {docs === null ? (
          <div style={{ fontSize: '0.82rem', color: C.muted, fontStyle: 'italic', padding: '0.5rem 0' }}>
            Cargando…
          </div>
        ) : total === 0 ? (
          <div style={{ fontSize: '0.82rem', color: C.muted, fontStyle: 'italic', padding: '0.5rem 0' }}>
            Aún no hay documentos subidos en tu ámbito del pliego.
          </div>
        ) : (
          <>
            {/* Buscador · una sola caja que filtra por archivo, ficha, rama o subidor */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.35rem 0.65rem',
                border: `1px solid ${q ? C.olive : C.border}`,
                borderRadius: 4, background: 'white', width: '100%', maxWidth: 380,
                boxSizing: 'border-box',
              }}>
                <MagnifyingGlass size={13} color={q ? C.olive : C.muted} />
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Buscar por archivo, ficha, rama o persona…"
                  style={{
                    flex: 1, border: 'none', outline: 'none',
                    fontSize: '0.8rem', color: C.text, background: 'transparent',
                    fontFamily: FONT,
                  }}
                />
                {q && (
                  <button
                    onClick={() => setQ('')}
                    aria-label="Limpiar búsqueda"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: C.muted, fontSize: '0.95rem', lineHeight: 1,
                      padding: '0 0.15rem',
                    }}>×</button>
                )}
              </div>
              {q && (
                <span style={{ marginLeft: '0.6rem', fontSize: '0.72rem', color: C.muted }}>
                  {docsFiltered.length} de {total}
                </span>
              )}
            </div>
            {docsFiltered.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: C.muted, fontStyle: 'italic', padding: '0.5rem 0' }}>
                Ningún documento coincide con «{q}».
              </div>
            ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', fontFamily: FONT }}>
              <thead>
                <tr style={{ textAlign: 'left', color: C.muted, borderBottom: `1px solid ${C.border}`, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Fecha</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Ficha</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Rama</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Archivo</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Subido por</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {docsFiltered.map(d => {
                  const f = fichaIdx[d.pliego_requisito_id]
                  return (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '0.4rem 0.5rem', color: C.muted, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600, color: C.text, whiteSpace: 'nowrap' }}>
                        {f?.criteria_code || '—'}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', color: C.muted, fontSize: '0.75rem' }}>
                        {ramaLabel(f?.rama)}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', color: C.text }}>
                        {d.filename}
                        {d.size_bytes ? <span style={{ color: C.muted, fontSize: '0.7rem', marginLeft: '0.35rem' }}>· {fmtBytes(d.size_bytes)}</span> : null}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', color: C.muted, fontSize: '0.75rem' }}>
                        {usersMap[d.user_id] || '—'}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                        <button onClick={() => abrirDoc(d.storage_path)}
                                title="Descargar"
                                style={{
                                  padding: '0.2rem 0.55rem', background: 'white', color: C.text,
                                  border: `1px solid ${C.border}`, borderRadius: 3,
                                  cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                                  fontFamily: FONT, display: 'inline-flex',
                                  alignItems: 'center', gap: '0.25rem',
                                }}>
                          <DownloadSimple size={11} /> Ver
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
            )}
          </>
        )}
      </div>
    </details>
  )
}

export default function PliegoRequisitos({
  projectId, projectName, isAdmin = false,
  pliegoRol = null, pliegoVisibleRamas = null,
}) {
  // Si el usuario es un invitado al pliego (arquitectura, ingenieria, contratista),
  // se aplica un filtro forzado por rama · no puede tocarlo desde la UI.
  // (pliegoRol se lee arriba en las props · lo usamos como indicador implícito.)
  const ramasPermitidas = Array.isArray(pliegoVisibleRamas) ? pliegoVisibleRamas : null

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Para invitados con una sola rama · la seleccionamos automáticamente.
  // Para invitados con varias ramas (arquitectura) o admin · null = ver todas.
  const [ramaSelected, setRamaSelected] = useState(
    ramasPermitidas && ramasPermitidas.length === 1 ? ramasPermitidas[0] : null
  )
  const [showAgenda, setShowAgenda]     = useState(false)
  const [estadoFilter, setEstadoFilter] = useState('todos') // 'todos' | 'pendiente' | 'en_revision' | 'auditado'
  const [faseFilter, setFaseFilter]     = useState('todas') // 'todas' | 'Diseño ejecutivo' | 'Licitación' | 'Obra' | 'Post-obra'
  const [respFilter, setRespFilter]     = useState('todos') // 'todos' | 'sin_asignar' | nombre exacto del responsable
  const [searchQuery, setSearchQuery]   = useState('')      // texto libre para buscar en cualquier campo de la ficha
  const [openCats, setOpenCats] = useState({})              // { 'Gestión': true, ... }

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    const { data, error } = await supabase
      .from('pliego_requisitos')
      .select('*')
      .eq('project_id', projectId)
      .order('rama', { ascending: true })
      .order('orden', { ascending: true })
    if (error) setError(error.message)
    setItems(data || [])
    setLoading(false)
  }, [projectId])

  useEffect(() => { load() }, [load])

  const update = async (id, patch) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i))
    const { error } = await supabase.from('pliego_requisitos').update(patch).eq('id', id)
    if (error) {
      alert('Error al guardar: ' + error.message)
      load()
    }
  }

  if (!projectId) {
    return (
      <div style={{ padding: '2rem', color: C.muted, fontFamily: FONT }}>
        Selecciona un proyecto para ver los Requisitos de Pliego.
      </div>
    )
  }

  // Para invitados al pliego · SIEMPRE filtramos a las ramas permitidas primero,
  // independientemente de lo que haya en ramaSelected. Así el usuario nunca ve
  // fichas de otras ramas aunque manipule state.
  const itemsAllowed = ramasPermitidas
    ? items.filter(i => ramasPermitidas.includes(i.rama))
    : items
  const baseByRama = ramaSelected ? itemsAllowed.filter(i => i.rama === ramaSelected) : itemsAllowed
  // Filtros en cadena · estado → fase → responsable → búsqueda de texto libre.
  const filteredByEstado = estadoFilter === 'todos'
    ? baseByRama
    : baseByRama.filter(i => i.estado === estadoFilter)
  const filteredByFase = faseFilter === 'todas'
    ? filteredByEstado
    : filteredByEstado.filter(i => (i.fase_exigencia || '') === faseFilter)
  const filteredByResp = respFilter === 'todos'
    ? filteredByFase
    : respFilter === 'sin_asignar'
      ? filteredByFase.filter(i => !i.responsable || !i.responsable.trim())
      : filteredByFase.filter(i => (i.responsable || '').trim() === respFilter)
  // Búsqueda insensible a mayúsculas/acentos en todos los campos de texto
  // relevantes: código, nombre, categoría, requisito, estado del proyecto,
  // mejora, comentario y responsable. Es suficiente para localizar cualquier
  // ficha por una palabra clave.
  const normalize = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
  const q = normalize(searchQuery.trim())
  const filtered = q
    ? filteredByResp.filter(i => {
        const hay = [
          i.criteria_code, i.criteria_name, i.categoria,
          i.requisito_breeam, i.estado_actual_proyecto,
          i.mejora_requerida, i.comentario_asesor, i.responsable,
        ].map(normalize).join(' ')
        return hay.includes(q)
      })
    : filteredByResp
  const ramaObj = ramaSelected ? RAMAS.find(r => r.id === ramaSelected) : null

  // Fases presentes en la rama seleccionada (para el dropdown).
  const fasesDisponibles = Array.from(new Set(baseByRama.map(i => i.fase_exigencia).filter(Boolean))).sort()
  // Responsables únicos asignados en la rama (se van poblando cuando el
  // asesor asigna nombres desde cada ficha).
  const responsablesDisponibles = Array.from(new Set(
    baseByRama.map(i => (i.responsable || '').trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'es'))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: FONT }}>
      <Header projectName={projectName} subtitleExtra={ramaObj ? ramaObj.label : null} />

      {error && (
        <div style={{ background: '#FFF1ED', border: `1px solid ${C.coral}`, borderRadius: 6, padding: '0.55rem 0.9rem', color: C.coral, fontSize: '0.82rem', fontFamily: FONT }}>
          Error: {error}
        </div>
      )}

      {loading && (
        <div style={{ ...CARD, textAlign: 'center', color: C.muted, fontFamily: FONT }}>
          Cargando requisitos…
        </div>
      )}

      {/* Lista desplegable de documentos subidos · siempre visible.
          Cada usuario ve solo los documentos de las fichas a las que tiene
          acceso (RLS de pliego_documentos filtra por rama). */}
      {!loading && <ListaDocumentosSubidos projectId={projectId} items={itemsAllowed} />}

      {/* Vista Agenda · fichas agrupadas por fecha límite.
          Para invitados al pliego pasamos itemsAllowed (ya filtrado por
          ramas permitidas) para que no vean fichas HAUS ni otras fuera
          de su alcance. */}
      {!loading && showAgenda && (
        <AgendaView
          items={itemsAllowed}
          isAdmin={isAdmin}
          onBack={() => setShowAgenda(false)}
          onOpenRama={(rama) => { setShowAgenda(false); setRamaSelected(rama) }}
          onUpdate={update}
        />
      )}

      {/* Vista 1 · selección de rama */}
      {!loading && !ramaSelected && !showAgenda && (
        <div style={CARD}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1.1rem' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.95rem', color: C.text, fontFamily: FONT, fontWeight: 600 }}>
                Selecciona el Equipo
              </h3>
              <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FONT }}>
                Cada agente tiene su propio conjunto de requisitos a incluir en su pliego.
              </div>
            </div>
            <button
              onClick={() => setShowAgenda(true)}
              title="Ver todas las fichas agrupadas por fecha límite"
              style={{
                padding: '0.5rem 0.9rem', background: C.olive, color: 'white',
                border: 'none', borderRadius: '4px', cursor: 'pointer',
                fontFamily: FONT, fontSize: '0.78rem', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                whiteSpace: 'nowrap',
              }}>
              <CalendarBlank size={14} /> Ver agenda
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.9rem' }}>
            {/* Para invitados al pliego · solo mostramos las ramas permitidas. */}
            {RAMAS.filter(r => !ramasPermitidas || ramasPermitidas.includes(r.id)).map(r => {
              const Icon = r.icon
              const bloque = itemsAllowed.filter(i => i.rama === r.id)
              const total = bloque.length
              const verificados = bloque.filter(i => i.estado === 'auditado').length
              const pendientes = bloque.filter(i => i.estado === 'pendiente').length
              return (
                <div key={r.id} onClick={() => setRamaSelected(r.id)}
                  style={{
                    border: `1px solid ${C.border}`, borderRadius: '6px',
                    padding: '1.15rem 1.05rem', background: C.bgAlt, cursor: 'pointer',
                    transition: 'transform 0.12s, box-shadow 0.12s',
                    display: 'flex', flexDirection: 'column',
                  }}
                  onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(59,47,47,0.09)' }}
                  onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
                >
                  {/* Cabecera unificada · misma altura y misma línea horizontal
                      independientemente de si la rama tiene logos o icono.
                      Así las 4 tarjetas quedan visualmente alineadas. */}
                  <div style={{
                    padding: '0.25rem 0.25rem',
                    borderBottom: `1px solid ${C.border}`,
                    marginBottom: '0.75rem',
                  }}>
                    {r.logos
                      ? <LogosRama logos={r.logos} height={RAMA_HEADER_HEIGHT} gap="1.1rem" />
                      : <IconoRama Icon={Icon} height={RAMA_HEADER_HEIGHT} />}
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT, marginBottom: '0.4rem' }}>
                    {r.label}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: C.muted, lineHeight: 1.45, minHeight: '2.5rem', fontFamily: FONT }}>
                    {r.hint}
                  </div>
                  <div style={{
                    marginTop: '0.85rem', paddingTop: '0.7rem',
                    borderTop: `1px dashed ${C.border}`,
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: '0.74rem', color: C.muted, fontFamily: FONT,
                  }}>
                    <span><strong style={{ color: C.text }}>{total}</strong> req.</span>
                    <span><strong style={{ color: C.coral }}>{pendientes}</strong> pdtes</span>
                    <span><strong style={{ color: C.olive }}>{verificados}</strong> ok</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Vista 2 · lista de requisitos de la rama */}
      {!loading && ramaSelected && (
        <div style={CARD}>
          {/* Cabecera de agente · título a la izquierda, acciones a la derecha */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: `1px solid ${C.border}` }}>
            {ramaObj && (() => {
              const RIcon = ramaObj.icon
              return (
                <>
                  {ramaObj.logos ? (
                    <div style={{ paddingRight: '0.5rem', borderRight: `1px solid ${C.border}`, marginRight: '0.5rem' }}>
                      <LogosRama logos={ramaObj.logos} height={32} gap="0.9rem" />
                    </div>
                  ) : (
                    <RIcon size={20} color={C.olive} weight="regular" />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT }}>
                      {ramaObj.label}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: C.muted, fontFamily: FONT }}>{ramaObj.hint}</div>
                  </div>
                </>
              )
            })()}
            <button onClick={load} title="Recargar" style={{
              padding: '0.35rem 0.7rem', background: 'transparent',
              color: C.muted, border: `1px solid ${C.border}`,
              borderRadius: '4px', cursor: 'pointer',
              fontFamily: FONT, fontSize: '0.76rem',
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            }}>
              <ArrowsClockwise size={12} /> Recargar
            </button>
            {/* Buscador de texto libre · filtra por cualquier palabra en
                cualquier campo de texto de la ficha. */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.28rem 0.55rem',
              border: `1px solid ${searchQuery ? C.olive : C.border}`,
              borderRadius: '4px', background: 'white',
            }}>
              <MagnifyingGlass size={12} color={searchQuery ? C.olive : C.muted} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Buscar…"
                style={{
                  border: 'none', outline: 'none', background: 'transparent',
                  fontFamily: FONT, fontSize: '0.76rem', color: C.text,
                  width: '150px',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  title="Limpiar búsqueda"
                  style={{
                    padding: 0, background: 'transparent', border: 'none',
                    color: C.muted, cursor: 'pointer', fontSize: '0.9rem',
                    lineHeight: 1, display: 'inline-flex', alignItems: 'center',
                  }}>×</button>
              )}
            </div>
            <button onClick={() => setRamaSelected(null)} title="Volver a la selección de equipos" style={{
              padding: '0.35rem 0.7rem', background: 'transparent',
              color: C.muted, border: `1px solid ${C.border}`,
              borderRadius: '4px', cursor: 'pointer',
              fontFamily: FONT, fontSize: '0.76rem',
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            }}>
              <CaretLeft size={12} /> Volver
            </button>
          </div>

          {/* Filtro por estado · chips clicables (contador + filtrado) */}
          {baseByRama.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 600, marginRight: '0.2rem', fontFamily: FONT }}>
                Filtrar:
              </span>
              {(() => {
                const totalTodos = baseByRama.length
                const active = estadoFilter === 'todos'
                return (
                  <button onClick={() => setEstadoFilter('todos')}
                    style={{
                      padding: '0.3rem 0.7rem', borderRadius: '4px',
                      background: active ? C.text : 'white',
                      color: active ? 'white' : C.muted,
                      border: `1px solid ${active ? C.text : C.border}`,
                      fontSize: '0.74rem', fontWeight: 600, fontFamily: FONT,
                      cursor: 'pointer',
                    }}>
                    <strong style={{ color: active ? 'white' : C.text }}>{totalTodos}</strong> Todos
                  </button>
                )
              })()}
              {ESTADOS.map(e => {
                const count = baseByRama.filter(i => i.estado === e.id).length
                const active = estadoFilter === e.id
                return (
                  <button key={e.id} onClick={() => setEstadoFilter(e.id)}
                    style={{
                      padding: '0.3rem 0.7rem', borderRadius: '4px',
                      background: active ? e.color : 'white',
                      color: active ? 'white' : e.color,
                      border: `1px solid ${e.color}`,
                      fontSize: '0.74rem', fontWeight: 600, fontFamily: FONT,
                      cursor: 'pointer',
                    }}>
                    <strong style={{ color: active ? 'white' : e.color }}>{count}</strong> {e.label}
                  </button>
                )
              })}

              {/* Dropdown fase de exigencia · separado por barra visual */}
              {fasesDisponibles.length > 0 && (
                <>
                  <div style={{ width: '1px', height: '20px', background: C.border, margin: '0 0.3rem' }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 600, fontFamily: FONT }}>
                      Fase:
                    </span>
                    <select
                      value={faseFilter}
                      onChange={e => setFaseFilter(e.target.value)}
                      style={{
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        border: `1px solid ${faseFilter === 'todas' ? C.border : C.olive}`,
                        background: 'white',
                        color: faseFilter === 'todas' ? C.text : C.olive,
                        fontFamily: FONT, fontSize: '0.74rem', fontWeight: 600,
                        cursor: 'pointer',
                      }}>
                      <option value="todas">Todas ({filteredByEstado.length})</option>
                      {fasesDisponibles.map(f => {
                        const count = filteredByEstado.filter(i => (i.fase_exigencia || '') === f).length
                        return <option key={f} value={f}>{f} ({count})</option>
                      })}
                    </select>
                  </label>

                  {/* Dropdown responsable · se rellena con los nombres únicos
                      que el asesor va asignando en las fichas. Siempre se
                      muestra (aunque esté vacío) para animar a que se
                      asignen responsables. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 600, fontFamily: FONT }}>
                      Responsable:
                    </span>
                    <select
                      value={respFilter}
                      onChange={e => setRespFilter(e.target.value)}
                      style={{
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        border: `1px solid ${respFilter === 'todos' ? C.border : C.olive}`,
                        background: 'white',
                        color: respFilter === 'todos' ? C.text : C.olive,
                        fontFamily: FONT, fontSize: '0.74rem', fontWeight: 600,
                        cursor: 'pointer',
                      }}>
                      <option value="todos">Todos ({filteredByFase.length})</option>
                      {responsablesDisponibles.map(r => {
                        const count = filteredByFase.filter(i => (i.responsable || '').trim() === r).length
                        return <option key={r} value={r}>{r} ({count})</option>
                      })}
                      {(() => {
                        const sinAsignar = filteredByFase.filter(i => !i.responsable || !i.responsable.trim()).length
                        return sinAsignar > 0 && (
                          <option value="sin_asignar">— Sin asignar ({sinAsignar})</option>
                        )
                      })()}
                    </select>
                  </label>
                </>
              )}
            </div>
          )}

          {filtered.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.muted, background: C.bgAlt, borderRadius: '6px', border: `1px dashed ${C.border}`, fontFamily: FONT }}>
              {estadoFilter === 'todos' && faseFilter === 'todas'
                ? 'No hay requisitos para este agente todavía.'
                : 'No hay requisitos que combinen los filtros seleccionados. Prueba a cambiar el estado o la fase arriba.'}
            </div>
          ) : (() => {
            // Agrupar por categoría BREEAM y respetar el orden natural del bloque
            const CAT_ORDER = ['Gestión', 'Salud y Bienestar', 'Energía', 'Transporte',
                               'Agua', 'Uso Suelo y Ecología', 'Materiales', 'Residuos',
                               'Contaminación', 'Innovación']
            const groups = new Map()
            filtered.forEach(it => {
              const cat = it.categoria || 'Sin categoría'
              if (!groups.has(cat)) groups.set(cat, [])
              groups.get(cat).push(it)
            })
            const ordered = [...groups.entries()].sort(
              (a, b) => (CAT_ORDER.indexOf(a[0]) === -1 ? 99 : CAT_ORDER.indexOf(a[0]))
                       - (CAT_ORDER.indexOf(b[0]) === -1 ? 99 : CAT_ORDER.indexOf(b[0]))
            )
            return (
              <div>
                {ordered.map(([cat, itemsCat]) => {
                  const isOpen = openCats[cat] || false
                  return (
                    <div key={cat} style={{
                      border: `1px solid ${C.border}`, borderRadius: '6px',
                      marginBottom: '0.6rem', background: C.bg, overflow: 'hidden',
                    }}>
                      <div
                        onClick={() => setOpenCats(o => ({ ...o, [cat]: !isOpen }))}
                        style={{
                          padding: '0.75rem 1rem', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '0.6rem',
                          background: isOpen ? '#EDE7DE' : '#F5F0EA',
                          borderBottom: isOpen ? `1px solid ${C.border}` : 'none',
                        }}>
                        {isOpen ? <CaretDown size={14} color={C.muted} /> : <CaretRight size={14} color={C.muted} />}
                        <div style={{ flex: 1, fontFamily: FONT, fontWeight: 700, color: C.text, fontSize: '0.9rem' }}>
                          {cat}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: '0.74rem', color: C.muted }}>
                          {itemsCat.length} {itemsCat.length === 1 ? 'ficha' : 'fichas'}
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '0.6rem' }}>
                          {itemsCat.map(item => (
                            <RequisitoCard key={item.id} item={item} onUpdate={update} isAdmin={isAdmin} />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}
